import React, { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { X, Info } from 'lucide-react';
import FormField from '@/components/common/form-fields/FormField';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Select } from '@/components/ui/Select';
import type { WebhookConfig } from '@/services/webhook.service';

// No resolver: the shared Input/Textarea apply their clean-text rule on top of the field rules below
interface WebhookFormData {
  webhook_name: string;
  webhook_description?: string;
  webhook_url: string;
  webhook_trigger_events: string[];
  webhook_method: string;
  webhook_api_key_format: string;
  webhook_api_key?: string;
  webhook_api_value?: string;
  webhook_content_type?: string;
  webhook_priority?: string;
  retry_enabled: boolean;
  retry_max_attempts: number;
  retry_backoff_seconds: number;
  timeout_seconds: number;
  webhook_secret?: string;
  include_signature: boolean;
  signature_header: string;
  is_active: boolean;
}

const isHttpUrl = (value?: string | null): boolean => {
  if (!value) return false;
  try {
    const { protocol } = new URL(value);
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false;
  }
};

// Same output the old schema produced: trimmed name, empty optional fields sent as undefined
const OPTIONAL_TEXT_FIELDS = [
  'webhook_description', 'webhook_api_key', 'webhook_api_value',
  'webhook_content_type', 'webhook_priority', 'webhook_secret',
] as const;

const normalizeWebhookData = (data: WebhookFormData): WebhookFormData => {
  const normalized = { ...data, webhook_name: data.webhook_name.trim() };
  OPTIONAL_TEXT_FIELDS.forEach((field) => {
    if (!normalized[field]) normalized[field] = undefined;
  });
  return normalized;
};

interface WebhookModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: WebhookFormData) => Promise<void>;
  webhook?: WebhookConfig | null;
  triggerEvents?: any[];
}

const httpMethods = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'] as const;
const contentTypes = [
  { value: 'application/json', label: 'JSON' },
  { value: 'application/xml', label: 'XML' },
  { value: 'application/x-www-form-urlencoded', label: 'Form URL Encoded' }
];
const priorities = [
  { value: 'LOW', label: 'Low' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'HIGH', label: 'High' },
  { value: 'CRITICAL', label: 'Critical' }
];

export default function WebhookModal({ isOpen, onClose, onSubmit, webhook, triggerEvents = [] }: WebhookModalProps) {
  const isEditMode = !!webhook;

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
    reset,
    watch
  } = useForm<WebhookFormData>({
    // Validate while typing so errors (e.g. clean-text) show under the field, not only on blur
    mode: 'onChange',
    defaultValues: {
      webhook_name: '',
      webhook_description: '',
      webhook_url: '',
      webhook_trigger_events: [],
      webhook_method: '',
      webhook_api_key_format: '',
      webhook_api_key: '',
      webhook_api_value: '',
      webhook_content_type: 'application/json',
      webhook_priority: 'MEDIUM',
      retry_enabled: true,
      retry_max_attempts: 3,
      retry_backoff_seconds: 60,
      timeout_seconds: 30,
      webhook_secret: '',
      include_signature: false,
      signature_header: 'X-Webhook-Signature',
      is_active: true
    }
  });

  // Watch form values
  const retryEnabled = watch('retry_enabled');
  const includeSignature = watch('include_signature');
  const apiKeyFormat = watch('webhook_api_key_format');

  useEffect(() => {
    if (isOpen) {
      if (isEditMode && webhook) {
        reset({
          webhook_name: webhook.webhook_name || '',
          webhook_url: webhook.webhook_url,
          webhook_trigger_events: webhook.webhook_trigger_events || [],
          webhook_method: webhook.webhook_method,
          webhook_content_type: webhook.webhook_content_type || 'application/json',
          webhook_priority: webhook.webhook_priority || 'MEDIUM',
          webhook_api_key_format: (webhook.webhook_api_key_format?.toLowerCase() || 'header') as any,
          webhook_api_key: webhook.webhook_api_key || '',
          webhook_api_value: webhook.webhook_api_value || '',
          webhook_description: webhook.webhook_description || '',
          is_active: webhook.is_active,
          retry_enabled: webhook.retry_enabled !== undefined ? webhook.retry_enabled : true,
          retry_max_attempts: webhook.retry_max_attempts || 3,
          retry_backoff_seconds: webhook.retry_backoff_seconds || 60,
          timeout_seconds: webhook.timeout_seconds || 30,
          webhook_secret: webhook.webhook_secret || '',
          include_signature: webhook.include_signature || false,
          signature_header: webhook.signature_header || 'X-Webhook-Signature'
        });
      } else {
        reset({
          webhook_name: '',
          webhook_description: '',
          webhook_url: '',
          webhook_trigger_events: [],
          webhook_method: '',
          webhook_api_key_format: '',
          webhook_api_key: '',
          webhook_api_value: '',
          webhook_content_type: 'application/json',
          webhook_priority: 'MEDIUM',
          retry_enabled: true,
          retry_max_attempts: 3,
          retry_backoff_seconds: 60,
          timeout_seconds: 30,
          webhook_secret: '',
          include_signature: false,
          signature_header: 'X-Webhook-Signature',
          is_active: true
        });
      }
    }
  }, [isOpen, isEditMode, webhook, reset]);

  const getTriggerEventLabel = (eventValue: string) => {
    const event = triggerEvents.find(e => e.value === eventValue);
    return event?.label || eventValue;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 common-modal-overlay">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b relative">
          <button
            onClick={onClose}
            className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 transition-colors"
            title="Close"
          >
            <X className="w-5 h-5" />
          </button>
          <h2 className="text-xl font-semibold text-gray-900 pr-8">
            {isEditMode ? 'Edit Webhook' : 'Create New Webhook'}
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Configure webhook endpoint and authentication
          </p>
        </div>

        <form onSubmit={handleSubmit((data) => onSubmit(normalizeWebhookData(data)))} className="p-6 space-y-4">
          {/* Webhook Name */}
          <Input<WebhookFormData>
            name="webhook_name"
            control={control}
            label="Webhook Name"
            helperText="A unique name to identify this webhook"
            required
            type="text"
            placeholder="e.g., User Enrollment Webhook"
            rules={{
              required: 'Webhook name is required',
              validate: (value: string) => (value || '').trim().length >= 3 || 'Name must be at least 3 characters',
            }}
          />

          {/* Webhook Description */}
          <Textarea<WebhookFormData>
            name="webhook_description"
            control={control}
            label="Webhook Description"
            helperText="Optional description of what this webhook does"
            rows={2}
            placeholder="Describe what this webhook does..."
          />

          {/* Trigger Events Multi-Select */}
          <Controller
            name="webhook_trigger_events"
            control={control}
            rules={{ validate: (value) => (value?.length ?? 0) > 0 || 'Select at least one trigger event' }}
            render={({ field }) => (
              <FormField
                label="Trigger Events"
                description="Select one or more events that will trigger this webhook"
                required
                error={errors.webhook_trigger_events?.message}
              >
                <div className="border border-gray-300 rounded-lg p-3 max-h-64 overflow-y-auto">
                  {triggerEvents.length > 0 ? (
                    Object.entries(
                      triggerEvents.reduce((acc: Record<string, any[]>, event: any) => {
                        const parts = event.value.split('_');
                        const category = parts[1] || 'Other';
                        const formattedCategory = category.charAt(0) + category.slice(1).toLowerCase();
                        if (!acc[formattedCategory]) acc[formattedCategory] = [];
                        acc[formattedCategory].push(event);
                        return acc;
                      }, {})
                    )
                    .sort(([a], [b]) => a.localeCompare(b))
                    .map(([category, events]) => (
                      <div key={category} className="mb-3 last:mb-0">
                        <p className="text-xs font-semibold text-gray-600 mb-2">{category}</p>
                        <div className="space-y-1">
                          {events.map((event) => (
                            <label
                              key={event.value}
                              className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={field.value?.includes(event.value)}
                                onChange={(e) => {
                                  const values = field.value || [];
                                  if (e.target.checked) {
                                    field.onChange([...values, event.value]);
                                  } else {
                                    field.onChange(values.filter(v => v !== event.value));
                                  }
                                }}
                                className="text-primary-600 focus:ring-primary-500 rounded"
                              />
                              <span className="text-sm text-gray-700">{event.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500">Loading events...</p>
                  )}
                </div>
                {field.value && field.value.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {field.value.map((eventValue) => (
                      <span
                        key={eventValue}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-brand-blue-100 text-brand-blue-700 text-xs font-medium rounded"
                      >
                        {getTriggerEventLabel(eventValue)}
                        <button
                          type="button"
                          onClick={() => {
                            field.onChange(field.value.filter(v => v !== eventValue));
                          }}
                          className="text-brand-blue-600 hover:text-brand-blue-800"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </FormField>
            )}
          />

          {/* URL and Method */}
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2">
              <Input<WebhookFormData>
                name="webhook_url"
                control={control}
                label="Webhook URL"
                required
                type="url"
                placeholder="https://api.example.com/webhook"
                rules={{
                  required: 'Webhook URL is required',
                  validate: (value: string) => isHttpUrl(value) || 'URL must be a valid URL starting with http:// or https://',
                }}
              />
            </div>

            <FormField
              label="Method"
              required
              error={errors.webhook_method?.message}
            >
              <Select<WebhookFormData>
                name="webhook_method"
                control={control}
                rules={{ required: 'HTTP method is required' }}
                options={httpMethods.map((method) => ({ value: method, label: method }))}
                placeholder="Select method"
                isSearchable={false}
              />
            </FormField>
          </div>

          {/* API Key Configuration */}
          <FormField
            label="API Key Format"
            required
            error={errors.webhook_api_key_format?.message}
          >
            <div className="grid grid-cols-3 gap-2">
              {(['header', 'query', 'body'] as const).map((format) => (
                <label
                  key={format}
                  className={`px-4 py-2 rounded-lg border transition-colors cursor-pointer text-center ${
                    watch('webhook_api_key_format') === format
                      ? 'bg-primary-50 border-primary-500 text-primary-700'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="radio"
                    {...register('webhook_api_key_format', { required: 'API key format is required' })}
                    value={format}
                    className="sr-only"
                  />
                  {format.charAt(0).toUpperCase() + format.slice(1)}
                </label>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Choose how the API key will be sent with the request
            </p>
          </FormField>

          {/* API Key */}
          <Input<WebhookFormData>
            name="webhook_api_key"
            control={control}
            label="API Key"
            helperText={
              apiKeyFormat === 'header' ? 'e.g., X-API-Key or Authorization' :
              apiKeyFormat === 'query' ? 'e.g., api_key' :
              'e.g., api_secret'
            }
            type="text"
            placeholder={
              apiKeyFormat === 'header' ? 'X-API-Key' :
              apiKeyFormat === 'query' ? 'api_key' :
              'api_secret'
            }
          />

          {/* API Value */}
          <div className="space-y-2">
            <Input<WebhookFormData>
              name="webhook_api_value"
              control={control}
              label="API Value"
              helperText="The actual API key or token value"
              type="password"
              placeholder={isEditMode ? "Leave blank to keep current value" : "Enter your API secret value"}
            />
            <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-blue-700">
                <strong>Security Notice:</strong> API values are encrypted before storage and cannot be displayed for security reasons.
                {isEditMode && " Leave blank to keep the current encrypted value, or enter a new value to update it."}
              </p>
            </div>
          </div>

          {/* Advanced Configuration Section */}
          <div className="border-t border-gray-200 pt-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Advanced Configuration</h3>

            {/* Content Type & Priority */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <FormField
                label="Content Type"
                error={errors.webhook_content_type?.message}
              >
                <Select<WebhookFormData>
                  name="webhook_content_type"
                  control={control}
                  options={contentTypes}
                  isSearchable={false}
                />
              </FormField>

              <FormField
                label="Priority Level"
                error={errors.webhook_priority?.message}
              >
                <Select<WebhookFormData>
                  name="webhook_priority"
                  control={control}
                  options={priorities}
                  isSearchable={false}
                />
              </FormField>
            </div>

            {/* Retry Configuration */}
            <div className="mb-4">
              <label className="flex items-center gap-2 mb-3">
                <input
                  type="checkbox"
                  {...register('retry_enabled')}
                  className="text-primary-600 focus:ring-primary-500 rounded"
                />
                <span className="text-sm font-medium text-gray-700">Enable Automatic Retry on Failure</span>
              </label>

              {retryEnabled && (
                <div className="grid grid-cols-2 gap-4 ml-6">
                  <FormField
                    label="Max Retry Attempts"
                    error={errors.retry_max_attempts?.message}
                  >
                    <input
                      type="number"
                      min="1"
                      max="10"
                      {...register('retry_max_attempts', {
                        valueAsNumber: true,
                        min: { value: 1, message: 'Must be at least 1' },
                        max: { value: 10, message: 'Must be at most 10' },
                      })}
                      className="w-full input-field"
                    />
                  </FormField>
                  <FormField
                    label="Backoff Delay (seconds)"
                    error={errors.retry_backoff_seconds?.message}
                  >
                    <input
                      type="number"
                      min="10"
                      max="300"
                      {...register('retry_backoff_seconds', {
                        valueAsNumber: true,
                        min: { value: 10, message: 'Must be at least 10 seconds' },
                        max: { value: 300, message: 'Must be at most 300 seconds' },
                      })}
                      className="w-full input-field"
                    />
                  </FormField>
                </div>
              )}
            </div>

            {/* Timeout */}
            <div className="mb-4">
              <FormField
                label="Request Timeout (seconds)"
                description="Maximum time to wait for webhook response"
                error={errors.timeout_seconds?.message}
              >
                <input
                  type="number"
                  min="5"
                  max="120"
                  {...register('timeout_seconds', {
                    valueAsNumber: true,
                    min: { value: 5, message: 'Must be at least 5 seconds' },
                    max: { value: 120, message: 'Must be at most 120 seconds' },
                  })}
                  className="w-full input-field"
                />
              </FormField>
            </div>

            {/* HMAC Signature */}
            <div className="mb-4">
              <label className="flex items-center gap-2 mb-3">
                <input
                  type="checkbox"
                  {...register('include_signature')}
                  className="text-primary-600 focus:ring-primary-500 rounded"
                />
                <span className="text-sm font-medium text-gray-700">Include HMAC Signature</span>
              </label>

              {includeSignature && (
                <div className="space-y-3 ml-6">
                  <div className="space-y-2">
                    <Input<WebhookFormData>
                      name="webhook_secret"
                      control={control}
                      label="Webhook Secret"
                      helperText="Secret key for HMAC signature generation"
                      type="password"
                      placeholder={isEditMode ? "Leave blank to keep current value" : "Enter secret key"}
                    />
                    <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-blue-700">
                        <strong>Security Notice:</strong> Webhook secrets are encrypted before storage and cannot be displayed for security reasons.
                        {isEditMode && " Leave blank to keep the current encrypted value, or enter a new value to update it."}
                      </p>
                    </div>
                  </div>
                  <Input<WebhookFormData>
                    name="signature_header"
                    control={control}
                    label="Signature Header Name"
                    helperText="Header name for the signature"
                    type="text"
                    placeholder="e.g., X-Webhook-Signature"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Status */}
          <Controller
            name="is_active"
            control={control}
            render={({ field }) => (
              <FormField
                label="Status"
                error={errors.is_active?.message}
              >
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={field.value === true}
                      onChange={() => field.onChange(true)}
                      className="text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm">Active</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={field.value === false}
                      onChange={() => field.onChange(false)}
                      className="text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm">Inactive</span>
                  </label>
                </div>
              </FormField>
            )}
          />

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-outline"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : isEditMode ? 'Update Webhook' : 'Create Webhook'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
